/**
 * Check if an error looks like a PrismaClientKnownRequestError by duck-typing.
 * This avoids importing the actual class which pulls in Node.js-specific code.
 */
export function isPrismaClientKnownRequestError(e: unknown): e is { code: string; name: string } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'name' in e &&
    e.name === 'PrismaClientKnownRequestError' &&
    'code' in e &&
    typeof e.code === 'string'
  );
}

/**
 * Most errors thrown by the server are instances of this class. For more
 * information on how to use it, see `docs/ERROR_HANDLING.md`.
 */
export class AirServerError extends Error {
  public userFacingMessage?: string;
  public message: AirServerErrorMessage;

  constructor(message: AirServerErrorMessage, userFacingMessage?: string, errorOptions?: ErrorOptions) {
    super(message, errorOptions);
    this.name = 'AirServerError';

    this.message = message;
    this.userFacingMessage = userFacingMessage;
  }

  static [Symbol.hasInstance](error: any): error is AirServerError {
    return error?.name === 'AirServerError';
  }

  /**
   * Wraps an unknown error into an `AirServerError`. If the error is already an
   * `AirServerError`, what happens depends on the value of `userFacingMessage`:
   * - if unspecified / `undefined`, then the error is used to make a new copy
   *   of the error, unless the error is not a plain object, in which case it is
   *   returned as-is.
   * - if `null`, a copy of the error without a user-facing message is returned.
   * - otherwise, a copy of the error with the user-facing message is returned.
   *
   * If the error is a Prisma error, it will attempt to map the error code to an
   * `AirServerErrorMessage` before creating an `AirServerError`.
   *
   * Otherwise, it will create an unknown `AirServerError`.
   *
   * @param e the error to wrap
   * @param userFacingMessage the user-facing message
   * @returns an `AirServerError`
   */
  static from(e: unknown, userFacingMessage?: string | null): AirServerError {
    if (e instanceof this) {
      if (userFacingMessage === undefined) {
        return Object.getPrototypeOf(e) === Object.prototype
          ? new this(e.message, e.userFacingMessage, { cause: e.cause })
          : e;
      }

      const cause = 'cause' in e ? e.cause : undefined;
      return new this(e.message, userFacingMessage ?? undefined, { cause });
    }

    if (isPrismaClientKnownRequestError(e)) {
      return new this(
        PrismaErrorCodesToAirServerMessage[e.code] ?? 'unexpected_db_or_prisma_error',
        userFacingMessage ?? undefined,
      );
    }

    return this.unknown(userFacingMessage ?? undefined, e);
  }

  /**
   * Returns an AirServerError with message 'internal_server_error' due to unknown error.
   * @param userFacingMessage optional user facing message
   * @param error the root cause error
   * @returns an AirServerError
   */
  static unknown(userFacingMessage?: string, error?: unknown): AirServerError {
    return new this('internal_server_error', userFacingMessage, { cause: error });
  }

  static unauthorized(userFacingMessage?: string, error?: unknown): AirServerError {
    return new this('unauthorized', userFacingMessage, { cause: error });
  }

  static forbidden(userFacingMessage?: string, error?: unknown): AirServerError {
    return new this('forbidden', userFacingMessage, { cause: error });
  }

  get statusCode(): number {
    return AirServerErrorMessageToStatusCode[this.message] ?? 500;
  }

  toString(): string {
    const head = `AirServerError: ${this.message}`;
    const additional = this.userFacingMessage ? `\n${this.userFacingMessage}` : '';
    return head + additional;
  }
}

const AirServerErrorMessageToStatusCode: Record<AirServerErrorMessage, number> = {
  unique_constraint_violation: 409,
  record_not_found: 404,
  dependent_record_not_found: 404,
  db_or_prisma_timeout: 500,
  db_connection_error: 500,
  invalid_value_for_field: 400,
  unexpected_db_or_prisma_error: 500,
  internal_server_error: 500,
  unauthorized: 401,
  forbidden: 403,
  bad_request: 400,
};

/**
 * A list of pre-defined AirServer error messages
 */
export const AirServerErrorMessages = [
  'unique_constraint_violation',
  'unique_constraint_violation',
  'record_not_found',
  'dependent_record_not_found',
  'db_or_prisma_timeout',
  'db_connection_error',
  'unexpected_db_or_prisma_error',
  'invalid_value_for_field',
  'internal_server_error',
  'unauthorized',
  'forbidden',
  'bad_request',
] as const;
export type AirServerErrorMessage = (typeof AirServerErrorMessages)[number];

// https://www.prisma.io/docs/orm/reference/error-reference#error-codes
export const PrismaErrorCodesToAirServerMessage: Record<string, AirServerErrorMessage> = {
  P2000: 'invalid_value_for_field',
  P2001: 'dependent_record_not_found',
  P2002: 'unique_constraint_violation',
  P2003: 'invalid_value_for_field',
  P2004: 'invalid_value_for_field',
  P2005: 'invalid_value_for_field',
  P2006: 'invalid_value_for_field',
  P2007: 'invalid_value_for_field',
  P2011: 'invalid_value_for_field',
  P2012: 'invalid_value_for_field',
  P2013: 'invalid_value_for_field',
  P2014: 'invalid_value_for_field',
  P2015: 'dependent_record_not_found',
  P2016: 'unexpected_db_or_prisma_error',
  P2017: 'dependent_record_not_found',
  P2018: 'dependent_record_not_found',
  P2020: 'unexpected_db_or_prisma_error',
  P2021: 'unexpected_db_or_prisma_error',
  P2022: 'unexpected_db_or_prisma_error',
  P2023: 'unexpected_db_or_prisma_error',
  P2024: 'db_or_prisma_timeout',
  P2025: 'dependent_record_not_found',
  P2026: 'unexpected_db_or_prisma_error',
  P2027: 'unexpected_db_or_prisma_error',
  P2028: 'unexpected_db_or_prisma_error',
  P2029: 'unexpected_db_or_prisma_error',
  P2030: 'unexpected_db_or_prisma_error',
  P2031: 'unexpected_db_or_prisma_error',
  P2033: 'invalid_value_for_field',
  P2034: 'unexpected_db_or_prisma_error',
  P2035: 'unexpected_db_or_prisma_error',
  P2036: 'db_connection_error',
  P2037: 'db_connection_error',
};
